/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ChannelIcon } from "@audacia-hq/shared/components";
import {
  faAngleLeft, faAngleRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Transition } from "@headlessui/react";
import dayjs, { Dayjs } from "dayjs";
import * as React from "react";
import {
  useCallback,
  useEffect, useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { estimateQueueItemStart, useQueue } from "../../context/QueueContext";
import { Expert, PlanningAvailabilityChannel, PlanningSlot } from "../../models/expert";
import Button from "../Button";

interface Props {
  expert: Expert;
  nbDays?: number;
  pxPerHour?: number;
  nbHours?: number;
  className?: string;
  showLegend?: boolean;
}

interface MergedSlot {
  startAt: Dayjs;
  duration: number;
  count?: number;
  ownSlot?: boolean;
}

const ExpertPlanning: React.FC<Props> = ({
  expert, nbDays, pxPerHour, nbHours, className, showLegend,
}: Props) => {
  const { t } = useTranslation("planning");
  const [displayNbDays, setDisplayNbDays] = useState<number>(nbDays || 2);
  const [displayHourPx, setDisplayHourPx] = useState<number>(pxPerHour || 64);
  const [displayNbHours, setDisplayNbHours] = useState<number>(nbHours || 6);

  const { rawMemberQueues } = useQueue();

  const wrapperElt = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState<Dayjs>(dayjs());
  const [displayDay, setDisplayDay] = useState<Dayjs>(now.startOf("day"));
  const [days, setDays] = useState<Dayjs[]>(new Array(displayNbDays).fill(0).map((_, k) => displayDay.add(k, "day")));
  const [computedSlots, setComputedSlots] = useState<MergedSlot[]>([]);
  const [availablities, setAvailabilites] = useState<PlanningSlot[]>([]);
  const [filledAvailablities, setFilledAvailabilites] = useState<PlanningSlot[]>([]);
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);

  useEffect(() => {
    const interval = setInterval(() => setNow(dayjs()), 60000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    setDisplayNbDays(nbDays || 2);
    setDisplayHourPx(pxPerHour || 64);
    setDisplayNbHours(nbHours || 6);
  }, [nbDays, pxPerHour, nbHours]);

  useEffect(() => {
    if (!wrapperElt?.current || hasScrolled) return;
    const offset = (now.diff(now.startOf("days"), "hour") - 1) * displayHourPx;
    wrapperElt.current.scrollTo(0, offset);
  }, [wrapperElt, hasScrolled]);

  useEffect(() => {
    if (!expert) return;
    const ownIdx = rawMemberQueues?.[expert.expertUid]?.items?.length ? rawMemberQueues[expert.expertUid].items.length - 1 : undefined;
    const slottedItems = estimateQueueItemStart(
      expert?.queue?.items || [],
      expert?.planning,
    ).items;
    const mergedItems = slottedItems.reduce((aggr, slot, idx) => {
      if (ownIdx === idx) {
        return [
          ...aggr,
          { ...slot, ownSlot: true, isStarted: slot.metadata.isEphemeral || slot.startedAt ? true : undefined },
        ];
      }
      if (slot.metadata.isEphemeral || slot.startedAt) return [...aggr, { ...slot, isStarted: true }];
      const last = aggr.at(-1);
      if (!last) return [slot];
      if (last.ownSlot || last.isStarted) return [...aggr, slot];
      const canMerge = !slot.startAt.isAfter(last.startAt.add(last.duration + 120, "second"));
      return canMerge
        ? [...aggr.slice(0, -1), { ...last, duration: last.duration + slot.duration + 120, count: (last.count || 1) + 1 }]
        : [...aggr, slot];
    }, []);

    setComputedSlots(mergedItems as MergedSlot[]);

    if (!expert?.planning) {
      setAvailabilites([]);
      setFilledAvailabilites([]);
    } else {
      const items = mergedItems || [];
      const { filled, unfilled } = expert.planning.slots
        .reduce((aggr, slot) => {
          const startDay = slot.start.startOf("day");
          const endDay = slot.end.startOf("day");
          const diff = endDay.diff(startDay, "day");
          if (diff === 0) return [...aggr, slot];

          const splitSlots = [...[...Array(diff).keys()].map((i) => startDay.add(i + 1, "day")),
          slot.end,
          ].reduce(({ start, slots }, end) => (
            { start: end, slots: [...slots, { ...slot, start, end }] }
          ), { start: slot.start, slots: [] }).slots;
          return [...aggr, ...splitSlots];
        }, [])
        .reduce<{ filled: PlanningSlot[]; unfilled: PlanningSlot[] }>((aggr, slot) => {
          if (items.find((i) => i.startAt.isBefore(slot.end) && i.startAt.add(i.duration, "second").isAfter(slot.start))) {
            return { ...aggr, filled: [...aggr.filled, slot] };
          }
          return { ...aggr, unfilled: [...aggr.unfilled, slot] };
        }, { filled: [], unfilled: [] });
      setAvailabilites(unfilled);
      setFilledAvailabilites(filled);
    }
  }, [expert, rawMemberQueues, now]);

  useEffect(() => {
    if (!displayDay) return;
    setDays(new Array(displayNbDays).fill(0).map((_, k) => displayDay.add(k, "day")));
  }, [displayDay, displayNbDays]);

  const changeDay = useCallback((increment: number) => {
    if (increment > 0) {
      if (displayDay.add(increment + displayNbDays, "day").isAfter(now.add(7, "day"))) return;
      setDisplayDay(displayDay.add(increment, "day"));
    }
    if (increment < 0) {
      if (displayDay.subtract(-increment, "day").isBefore(now.startOf("day"))) return;
      setDisplayDay(displayDay.subtract(-increment, "day"));
    }
  }, [days, displayNbDays, displayDay]);

  const slotPeopleLabel = ({ ownSlot, isStarted, count }: { ownSlot?: boolean, isStarted?: boolean, count?: number }) => {
    if (ownSlot) return isStarted ? t("slot.youStarted") : t("slot.you");
    if (isStarted) return t("slot.started");
    if (!count || count === 1) return t("slot.other");
    return t("slot.others", { nb: count });
  };

  return (
    <div className={className || ""}>

      <Transition
        show={!!showLegend}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 -translate-y-2"
        enterTo="transform opacity-200 translate-y-0"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-200 translate-y-0"
        leaveTo="transform opacity-0 -translate-y-2"
      >
        <div className="p-4 rounded-sm mb-4 bg-slate-50 w-fit">
          <div className="grid grid-cols-1 gap-y-2 gap-x-4">

            <div className="grid grid-rows-1 grid-cols-[3rem_minmax(0,_1fr)] gap-4">
              <div className="flex justify-center items-center gap-1 h-6 bg-[#84CC16] text-xs text-white rounded">
                <ChannelIcon channel="PHONE" />
                <ChannelIcon channel="VIDEO" />
                <ChannelIcon channel="LIVECHAT" />
              </div>
              <div>
                {t("legend.availability")}
              </div>
            </div>

            <div className="grid grid-rows-1 grid-cols-[3rem_minmax(0,_1fr)] gap-4">
              <div className="flex justify-center items-center h-6 bg-[#F97316] rounded" />
              <div>
                {t("legend.queued")}
              </div>
            </div>

            {computedSlots.find((s) => s.ownSlot) && (
              <div className="grid grid-rows-1 grid-cols-[3rem_minmax(0,_1fr)] gap-4">
                <div className="flex justify-center items-center h-6 bg-white border-y-2 border-x-8 border-[#F97316] rounded" />
                <div>
                  {t("legend.yourPosition")}
                </div>
              </div>
            )}
          </div>
        </div>
      </Transition>

      {/* header */}
      <div
        className="pl-14 bg-white/5 border-gray-300 border-b relative"
      >
        <div className="text-xl">
          {displayDay.isAfter(now) && (
            <div className="absolute top-0 left-0 bottom-0 flex justify-center items-center">
              <div>
                <Button size="small" className="text-sm" onClick={() => setDisplayDay(now.startOf("day"))}>
                  {t("common:today")}
                </Button>
              </div>
              <div>
                <button
                  type="button"
                  className="w-12"
                  onClick={() => changeDay(-1)}
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
              </div>
            </div>
          )}
          {displayDay.add(displayNbDays, "day").isBefore(now.startOf("day").add(7, "day")) && (
            <div
              className="absolute top-0 right-0 bottom-0 w-12 flex justify-center items-center cursor-pointer"
              onClick={() => changeDay(+1)}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </div>
          )}

          <div
            className="-mr-px divide-x divide-gray-200 text-sm leading-6 grid"
            style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr)) auto` }}
          >
            {/* <div className="absolute inset-0 right-inherit" /> */}

            {/* <div className="col-end-1 w-14" /> */}
            {days.map((date, index) => (
              <div
                key={index}
                className="py-3 flex flex-col items-center justify-center font-medium"
              >
                <div className="uppercase">
                  {t(`days.${date.day()}`)}
                </div>
                <div className="text-xl">
                  {date.format("DD.MM")}
                </div>
              </div>

            ))}
          </div>
        </div>

      </div>

      {/* planning wrapper */}
      <div
        ref={wrapperElt}
        className="overflow-x-hidden overflow-y-scroll no-scrollbar"
        style={{ height: `${displayHourPx * displayNbHours}px` }}
        onScroll={() => setHasScrolled(true)}
      >
        {/* planning */}
        <div>
          <div
            className="flex flex-auto flex-col overflow-hidden bg-white container-scroll relative"
          >
            <div
              className="flex flex-col max-w-full"
            >
              <div className="flex flex-auto">
                <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
                <div
                  className="grid flex-auto grid-cols-1 grid-rows-1 planning-bounds"
                >
                  {/* Horizontal lines */}
                  <div
                    className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-200"
                    style={{ gridTemplateRows: `repeat(48, ${displayHourPx / 2}px)` }}
                  >
                    <div>
                      <div className="sticky left-0 z-20 -mt-2.5 -ml-14 w-14 pr-2 text-right text-xs  text-gray-400" />
                    </div>
                    <div />
                    {new Array(23).fill(0).map((_, hour) => (
                      <React.Fragment key={hour}>
                        <div
                          className="relative select-none"
                        >
                          <div className="sticky left-0 z-20 -mt-2.5 -ml-14 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                            {hour + 1}
                            :00
                          </div>

                        </div>
                        <div />
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Vertical lines */}
                  <div
                    className="col-start-1 col-end-2 row-start-1 grid-rows-1 divide-x divide-gray-200 grid"
                    style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr)) auto` }}
                  >
                    {days.map((d) => (<div key={d.toISOString()} className="row-span-full" />))}
                  </div>

                  {/* Events */}

                  <ol
                    className="relative z-10 col-start-1 col-end-2 row-start-1 grid"
                    style={{
                      gridTemplateRows: `repeat(96, ${displayHourPx / 4}px) auto`,
                      gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr)) auto`,
                    }}
                  >
                    {now.startOf("day").isSame(days[0]) && (
                      <div
                        className="relative col-start-1 col-span-1 h-[2px] bg-red-500 z-50 pointer-events-none"
                        style={{
                          top: `${((now.minute() / 60) * displayHourPx) + 1}px`,
                          gridRow: `${(1 + Math.floor((now.hour() * 60) / 15)) || 1} / span 1`,
                        }}
                      />
                    )}

                    {availablities
                      .filter((a) => !a.start.isBefore(days[0]) && !a.start.isAfter(days.at(-1).add(1, "day")))
                      .map((a) => {
                        const start = 1 + Math.floor((a.start.hour() * 60 + a.start.minute()) / 15);
                        const span = Math.ceil(a.end.diff(a.start, "minutes") / 15);
                        const colStart = days.findIndex((d) => !a.start.isBefore(d)
                          && a.start.isBefore(d.add(1, "day")));
                        return (
                          <div
                            key={a.start.toISOString()}
                            className="relative"
                            style={{
                              gridRow: `${start || 1} / span ${span || 1}`,
                              gridColumn: `${colStart + 1} / span 1`,
                            }}
                          >
                            <div className="absolute inset-1 bg-[#84CC16] rounded-xs flex justify-center items-center text-lg text-white gap-4">
                              {["PHONE", "VIDEO", "LIVECHAT"].map(
                                (c) => (a.channels.includes(c as PlanningAvailabilityChannel) ? (<ChannelIcon key={c} channel={c as any} />) : (<React.Fragment key={c} />)),
                              )}
                            </div>

                          </div>
                        );
                      })}

                    {filledAvailablities
                      .filter((a) => !a.start.isBefore(days[0]) && !a.start.isAfter(days.at(-1).add(1, "day")))
                      .map((a) => {
                        const start = 1 + Math.floor((a.start.hour() * 60 + a.start.minute()) / 15);
                        const span = Math.ceil(a.end.diff(a.start, "minutes") / 15);
                        const colStart = days.findIndex((d) => !a.start.isBefore(d)
                          && a.start.isBefore(d.add(1, "day")));
                        return (
                          <div
                            key={a.start.toISOString()}
                            className="relative"
                            style={{
                              gridRow: `${start || 1} / span ${span || 1}`,
                              gridColumn: `${colStart + 1} / span 1`,
                            }}
                          >
                            <div className="absolute w-6 top-0 left-0 bottom-0 bg-[#84CC16]">
                              <div className="relative top-2 bg-[#84CC16] rounded-xs flex flex-col justify-start text-xs items-center text-white gap-2">
                                {["PHONE", "VIDEO", "LIVECHAT"].map(
                                  (c) => (a.channels.includes(c as PlanningAvailabilityChannel) ? (<ChannelIcon key={c} channel={c as any} />) : (<React.Fragment key={c} />)),
                                )}
                              </div>
                            </div>

                          </div>
                        );
                      })}

                    {computedSlots
                      .filter((s) => !s.startAt.isBefore(days[0]) && !s.startAt.isAfter(days.at(-1).add(1, "day")))
                      .map((s) => {
                        const start = 1 + Math.floor((s.startAt.hour() * 60 + s.startAt.minute()) / 15);
                        const colStart = days.findIndex((d) => !s.startAt.isBefore(d)
                          && s.startAt.isBefore(d.add(1, "day")));
                        const tooSmall = s.duration < 20 * 60;
                        return (
                          <div
                            key={s.startAt.toISOString()}
                            className="relative flex flex-row flex-nowrap"
                            style={{
                              gridRow: `${start || 1} / span 1`,
                              gridColumn: `${colStart + 1} / span 1`,
                            }}
                          >
                            <div className="w-1/12 grow-0">
                              {" "}
                            </div>
                            <div className="relative flex-grow">
                              <div
                                className={`absolute inset-1 rounded-xs flex py-1 px-2
                                ${tooSmall ? " items-center group z-[1] hover:!h-8 hover:z-50 hover:translate-y-[-25%] transition-all delay-200 ease-out" : ""} overflow-hidden
                                ${s.ownSlot ? " z-[2] bg-white border-y-2 border-x-8 border-[#F97316] text-[#F97316]" : " bg-[#F97316] text-white"}
                               `}
                                style={{
                                  height: `${(s.duration / 3600) * displayHourPx + 2.5}px`,
                                  top: `${(s.startAt.minute() / 60) * displayHourPx}px`,
                                  lineHeight: s.ownSlot ? `${((s.duration) / 3600) * displayHourPx}px` : "initial",
                                }}
                              >
                                <div
                                  className={`flex w-full justify-between flex-nowrap
                                  ${tooSmall ? "items-center transition delay-200 ease-in-out group-hover:!opacity-100" : ""}`}
                                  style={{ opacity: tooSmall ? 0 : 1 }}
                                >
                                  <div>{slotPeopleLabel(s)}</div>
                                  {s.duration >= 40 * 60
                                    ? (
                                      <div className="flex flex-col h-full justify-between items-end">
                                        <div>{s.startAt.format("LT")}</div>
                                        <div>{s.startAt.add(s.duration, "second").format("LT")}</div>
                                      </div>
                                    )
                                    : (<div>{`${s.startAt.format("LT")} - ${s.startAt.add(s.duration, "second").format("LT")}`}</div>)}

                                </div>
                              </div>
                            </div>

                          </div>
                        );
                      })}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ExpertPlanning;
